import { useEffect, useState, useRef } from "react";
import { stockSearch } from "../../api/GetJewellery";
import { useFilters } from "../../context/FilterContext";
import ProductCard from "./ProductCards";
import { useAuth } from "../../context/AuthContext";
import { Dialog } from 'primereact/dialog';
import { Link } from "react-router-dom";
import { AddToCart, GetMyCart } from "../../api/cartService";
import { useCount } from "../../context/CountContext";
import CommonPopUpModal from "../../components/Common/CommonPopUpModal";
import EnquiryModal from "../Enquiry/EnquiryModal";
import "../../../src/css/CommonPopUp.css";  

export default function VaultCards({ products, setProducts, setTotalProducts }) {
    const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";
    const { user, showToast } = useAuth();
    const { selectedFilters, selectedCategory, sort, hasMore, setContextData } = useFilters();
    const loaderRef = useRef(null);
    const [page, setPage] = useState(1);
    const [isFetching, setIsFetching] = useState(false);
    const [addingProductId, setAddingProductId] = useState(null);
    const pageSize = 50;
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cartItemIds, setCartItemIds] = useState(new Set());
    const [isCommonModalOpen, setIsCommonModalOpen] = useState(false);
    const [commonModalMessage, setCommonModalMessage] = useState("");
    const [commonModalTitle, setCommonModalTitle] = useState("");
    const [commonModalPageType, setCommonModalPageType] = useState("");

    const {
        fetchCartCount,
        enquiryItems,
        setEnquiryItems,
        enquiryRemark,
        setEnquiryRemark,
        isEnquiryOpen,
        setIsEnquiryOpen,
        submittingEnquiry,
        addToEnquiry,
        submitEnquiry: submitEnquiryGlobal
    } = useCount();

    const [copied, setCopied] = useState(false);

    const handleOpenQuickView = (productDetails) => {
        setSelectedProduct(productDetails);
        setIsModalOpen(true);
    };

    const fetchProducts = async (pageNo = 1) => {
        if (isFetching) return;

        setIsFetching(true);
        try {
            const payload = {
                Party_ID: user?.SelectedCustomer_ID ?? selectedFilters.Party_ID,
                Category: String(selectedCategory || ""),
                SubCategory: "",
                Metal: (selectedFilters["Metal Tone"] || []).map(x => x.value).join("~"),
                GoldQuality: (selectedFilters["Gold Quality"] || []).map(x => x.value).join("~"),
                DiamondShape: (selectedFilters["Diamond Shape"] || []).map(x => x.value).join("~"),
                DiamondQuality: (selectedFilters["Diamond Quality"] || []).map(x => x.value).join("~"),
                Collection: (selectedFilters["Collection"] || []).map(x => x.value).join("~"),
                ItemSize: (selectedFilters["Item Size"] || []).map(x => x.value).join("~"),
                StyleNo: String(selectedFilters["By Ring Style"] || ""),
                FromPrice: String(selectedFilters["Price"]?.from ?? "0"),
                ToPrice: String(selectedFilters["Price"]?.to ?? "10000000"),
                FromCarat: String(selectedFilters["Carat"]?.from ?? "0"),
                ToCarat: String(selectedFilters["Carat"]?.to ?? "100"),
                FromGoldWeight: String(selectedFilters["Gold Weight"]?.from ?? "0"),
                ToGoldWeight: String(selectedFilters["Gold Weight"]?.to ?? "250"),
                PageNo: String(pageNo),
                PageSize: String(pageSize),
                Style_ID: "",
                SortBy: String(sort) || "",
            };
            const response = await stockSearch(payload);
            const list = response?.Products || [];
            const totalRecords = response?.Pagination?.[0]?.Total_Record || 0;
            const totalPages = response?.Pagination?.[0]?.Total_Page || 1;

            setTotalProducts(totalRecords);

            setProducts(prev =>
                pageNo === 1 ? list : [...prev, ...list]
            );

            setContextData({
                hasMore: pageNo < totalPages
            });

        } catch (error) {
            console.log("Fetch error", error);
        } finally {
            setIsFetching(false);
        }
    };

    const fetchCartItems = async () => {
        if (user?.SelectedCustomer_ID === undefined) return;
        try {
            const res = await GetMyCart({
                Customer_ID: user?.SelectedCustomer_ID,
                StockCategory: "STOCK",
                BasketType: "STOCK"
            });
            const items = res?.CartItems || res?.ROW || [];
            const ids = new Set(items.map(item => String(item.Style_ID)));
            setCartItemIds(ids);
        } catch (error) {
            console.error("Error fetching cart items:", error);
        }
    };

    const isValidEnquiryNo = (val) => {
        if (!val) return false;
        return /^[A-Z]{1,5}\/\d{1,4}\/\d{1,4}$/i.test(val);
    };

    const resetEnquiryState = () => {
        setIsEnquiryOpen(false);
    };

    const removeEnquiryItem = (productId) => {
        setEnquiryItems(prev => prev.filter(p => p.Style_ID !== productId));
    };

    const copyEnquiryNo = () => {
        if (!isValidEnquiryNo(commonModalTitle)) return;
        navigator.clipboard.writeText(commonModalTitle).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const submitEnquiry = async () => {
        const markLocalEnquiry = (styleIds) => {
            const ids = Array.isArray(styleIds) ? styleIds : [styleIds];
            setProducts(prev =>
                (prev || []).map(p =>
                    ids.includes(p.Style_ID) ? { ...p, IS_AddedInEnquiry: "YES" } : p
                )
            );
        };

        const res = await submitEnquiryGlobal(user?.SelectedCustomer_ID, markLocalEnquiry);
        if (!res.success && res.message) {
            setCommonModalTitle("Error");
            setCommonModalMessage(res.message);
            setCommonModalPageType("error");
            setIsCommonModalOpen(true);
        }
    };

    useEffect(() => {
        setProducts([]);
        setPage(1);
        fetchProducts(1);
        fetchCartItems();
    }, [selectedFilters, selectedCategory, sort, user?.SelectedCustomer_ID]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isFetching) {
                    setPage(prevPage => {
                        const nextPage = prevPage + 1;
                        fetchProducts(nextPage);
                        return nextPage;
                    });
                }
            },
            { rootMargin: "200px" }
        );
        const current = loaderRef.current;
        if (current) observer.observe(current);
        return () => {
            if (current) observer.unobserve(current);
        };
    }, [hasMore, isFetching]);

    const handleAddToCart = async (product) => {
        if (!user?.SelectedCustomer_ID || user?.SelectedCustomer_ID === 0) {
            showToast({
                title: "Validation!",
                message: "Please select a specific customer to add items to cart.",
                type: "warning",
            });
            return;
        }
        setAddingProductId(product.Style_ID);
        try {
            const payload = {
                Party_ID: user?.SelectedCustomer_ID,
                Style_ID: product.Style_ID,
                JewelCode: product.JewelCode,
                StyleNo: product.StyleNo,
                Quantity: "1",
            };
            const res = await AddToCart(payload);
            const messageObj = res?.ZMESSAGE?.[0];
            if (messageObj?.RETURNCODE === "1") {
                showToast({
                    title: "Success!",
                    message: messageObj?.RETURNMESSAGE || `Style ${product.StyleNo} added to cart`,
                    type: "success",
                });
                if (user?.SelectedCustomer_ID !== undefined) {
                    fetchCartCount(user.SelectedCustomer_ID);
                }
                setProducts(prev => prev.map(p =>
                    p.Style_ID === product.Style_ID ? { ...p, IS_AddedInCart: "YES" } : p
                ));
                setCartItemIds(prev => new Set([...prev, String(product.Style_ID)]));
            } else {
                showToast({
                    title: "Error!",
                    message: messageObj?.RETURNMESSAGE || "Failed to add product to cart",
                    type: "error",
                });
            }
        } catch (error) {
            console.error("Error to adding in cart", error);
        } finally {

        }
    };

    const ProductSkeleton = () => (
        <div className="product-card skeleton-card">
            <div className="skeleton skeleton-image"></div>
            <div className="skeleton skeleton-text small"></div>
            <div className="skeleton skeleton-text"></div>
            <div className="skeleton skeleton-text small"></div>
            <div className="skeleton skeleton-btn"></div>
        </div>
    );

    return (
        <>
            <section>
                <div className="vault-access-container">
                    <div className="product-grid">
                        {products.map((item, index) => (
                            <ProductCard
                                key={`${item.Style_ID}-${item.Rno}`}
                                Style_ID={item.Style_ID}
                                Image1={item.Image1}
                                Line1={item.Line1}
                                Line2={item.Line2}
                                LineAmount={item.LineAmount}
                                LineDiscount={item.LineDiscount}
                                PriceRemark={item.PriceRemark}
                                StyleNo={item.StyleNo}
                                IS_AddedInCart={item.IS_AddedInCart}
                                IS_AddedInEnquiry={item.IS_AddedInEnquiry}
                                isInCart={item.IS_AddedInCart?.toUpperCase() === "YES" || cartItemIds.has(String(item.Style_ID))}
                                isInEnquiry={item.IS_AddedInEnquiry?.toUpperCase() === "YES"}
                                NewArrivalStatus={item.NewArrivalStatus}
                                Status={item.Status}
                                addingToCart={addingProductId === item.Style_ID}
                                handleAddToCart={() => handleAddToCart(item)}
                                onQuickViewOpen={handleOpenQuickView}
                                product={item}
                                 onEnquiry={() => {
                                    if (!user?.SelectedCustomer_ID || user?.SelectedCustomer_ID === 0) {
                                        showToast({
                                            title: "Validation!",
                                            message: "Please select a specific customer for enquiry.",
                                            type: "warning",
                                        });
                                        return;
                                    }
                                    addToEnquiry(item);
                                }}
                            />
                        ))}

                        {isFetching && Array.from({ length: 12 }).map((_, i) => (<ProductSkeleton key={i} />))}
                    </div>

                    <Dialog
                        visible={isModalOpen}
                        className="quickview-dialog"
                        onHide={() => setIsModalOpen(false)}
                        draggable={false}
                        dismissableMask
                        showHeader={false}
                        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
                    >
                        {selectedProduct && (
                            <div className="qv-container">
                                <div className="qv-image-section">
                                    <img
                                        src={selectedProduct.Image1 || DEFAULT_IMAGE}
                                        alt={selectedProduct.StyleNo}
                                        className="qv-main-img"
                                        onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
                                    />
                                </div>

                                <div className="qv-details-section">
                                    <div className="qv-heading">Quick View: </div>
                                    <div className="qv-ref">
                                        <label>Style No: </label>
                                        <span> {selectedProduct.StyleNo}</span>
                                    </div>
                                    <div className="qv-ref">
                                        <span> {selectedProduct.LineAmount}</span>
                                    </div>
                                    <div className="qv-header-row">
                                        <label>Product Name: </label>
                                        <h2 className="qv-title">{selectedProduct.Line1}</h2>
                                    </div>

                                    <hr className="qv-divider" />

                                    <div className="qv-grid-info">
                                        <div className="qv-info-item full-width">
                                            <label>GOLD PURITY</label>
                                            <span>{selectedProduct.MetalQuality || "-"} / {selectedProduct.MetalTone || "-"}</span>
                                        </div>
                                        <div className="qv-info-item full-width">
                                            <label>GOLD WEIGHT</label>
                                            <span>{selectedProduct.TotalNetWt || "-"} G</span>
                                        </div>
                                        <div className="qv-info-item full-width">
                                            <label>DIMENSION</label>
                                            <span>{selectedProduct.ItemSize || "-"}</span>
                                        </div>
                                    </div>

                                    <div className="qv-footer">
                                        <Link to={`/product-detail/${selectedProduct.Style_ID}`} className="qv-details-link">
                                            VIEW MORE DETAILS <span className="qv-arrow">→</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Dialog>
                    <div ref={loaderRef}></div>
                </div>
            </section>
            <CommonPopUpModal
                isOpen={isCommonModalOpen}
                onClose={() => setIsCommonModalOpen(false)}
                message={commonModalMessage}
                pageType={commonModalPageType || "error"}
                title={
                    commonModalPageType === "enquiry" ? (
                        <>
                            <div className="popup-subtitle">
                                Thanks for reaching out to us.
                            </div>

                            <div className="popup-enquiry-label">
                                Your Enquiry No is
                            </div>

                            <div
                                className={`popup-enquiry-no ${isValidEnquiryNo(commonModalTitle) ? "copyable" : ""}`}
                                onClick={copyEnquiryNo}
                                title={isValidEnquiryNo(commonModalTitle) ? "Click to copy" : ""}
                            >
                                <span>{commonModalTitle}</span>

                                {isValidEnquiryNo(commonModalTitle) && (
                                    <span className={`copy-wrapper ${copied ? "copied" : ""}`}>
                                        <i className="pi pi-copy copy-icon" />
                                        {copied && (
                                            <span className="copied-tooltip">
                                                Copied!
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>
                        </>
                    ) : (
                        commonModalTitle
                    )
                }
            />

            <EnquiryModal
                isOpen={isEnquiryOpen}
                toggle={resetEnquiryState}
                items={enquiryItems}
                remark={enquiryRemark}
                setRemark={setEnquiryRemark}
                onRemoveItem={removeEnquiryItem}
                onSubmit={submitEnquiry}
                submitting={submittingEnquiry}
                title="Product Enquiry"
            />
        </>
    );
}